/* eslint-disable react/display-name */
import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import lodashKeyBy from "lodash/keyBy";
import lodashGet from "lodash/get";
import { RadioButtonGroup, RadioButton } from "material-ui/RadioButton";
import Grid from "@material-ui/core/Grid";
import { IAllocationDimension } from "helpers/allocation-dimension";
import Dialog from "components/dialog";
import Button from "components/form/button";
import Label from "components/form/label";
import { BrandDropdown } from "components/product-collection/product-collection-header-components/brand-dropdown";
import { IMerchant } from "containers/management/management-types";
import { ICreateCollectionParams } from "actions/collections";
import { SelectWrapper } from "components/common/multi-select";
import { ValuesSelect } from "./values-select";
import { AssortmentType } from "containers/folders/types/base";
import { DATATEST } from "constants/integration";

const muiStyles = {
    popover: {
        maxWidth: "400px",
        padding: "12px",
        overflowY: "auto",
    },
    label: {
        marginTop: "16px",
    },
};

interface IProps {
    assortmentType: AssortmentType;
    brandEntities: any;
    brandIds: string[];
    isOpen?: boolean;
    // doors: IDoor[];
    merchantAllocationDimensions: IAllocationDimension[];
    merchantConnections?: any[];
    merchants: IMerchant[];
    onCreateNewCollection: (params: ICreateCollectionParams) => void;
    onDialogClose: () => void;
    onMerchantSelect: (merchantId: string) => void;
    isFetchingBrands: boolean;
    onFetchBrands: (options?) => void;
}

export const isThereAtLeastOneDimensionValueChecker = (
    excludedAllocationDimensionValues,
    merchantAllocationDimensions
) => {
    return Object.keys(excludedAllocationDimensionValues).every((excludedAllocationDimensionKey) => {
        const currentMerchantAllocationDimension = merchantAllocationDimensions.find(
            (merchantAllocationDimension) => merchantAllocationDimension.id === excludedAllocationDimensionKey
        );
        let dimensionValues = [];
        if (currentMerchantAllocationDimension) {
            dimensionValues = currentMerchantAllocationDimension.dimension_values.filter(
                (dimension_value) => dimension_value.active
            );
        }

        return excludedAllocationDimensionValues[excludedAllocationDimensionKey].length !== dimensionValues.length;
    });
};

export const handleOnAllocationDimensionValuesChangeFactory = (
    allocationDimension: IAllocationDimension,
    setter: any
) => {
    return (selectedAllocationDimensionValuesIds) => {
        // If allocation dimension values are not selected, exclude those.
        const unselectedAllocationDimensionIds = allocationDimension.dimension_values.reduce((acc, dimensionValue) => {
            if (dimensionValue.active && !selectedAllocationDimensionValuesIds.includes(dimensionValue.id)) {
                acc.push(dimensionValue.id);
            }
            return acc;
        }, []);

        setter((existing) => {
            return {
                ...existing,
                [allocationDimension.id]: unselectedAllocationDimensionIds,
            };
        });
    };
};

export const StartAssortmentModal: React.FC<IProps> = React.memo((props: IProps) => {
    const {
        assortmentType,
        brandEntities,
        brandIds,
        isOpen,
        merchantAllocationDimensions = [],
        merchantConnections,
        merchants,
        onCreateNewCollection,
        onDialogClose,
        onMerchantSelect,
        isFetchingBrands,
        onFetchBrands,
    } = props;

    const [selectedRetailer, setSelectedRetailer] = React.useState<string | null>(
        merchantConnections?.[0].merchant_id || null
    );
    const [selectedBrandId, setSelectedBrandId] = React.useState<string | null>(null);
    const [brandPopoverOpen, setBrandPopoverOpen] = React.useState<boolean>(false);
    const [excludedAllocationDimensionValues, setExcludedAllocationDimensionValues] = React.useState<{
        [allocationDimensionId: string]: string[];
    }>({});
    const anchorElRef = React.useRef<HTMLDivElement>();

    const retailerOptions = React.useMemo(() => {
        const merchantMap = lodashKeyBy(merchants, "merchant_id");
        return merchantConnections.map((merchantConnection) => {
            return (
                <RadioButton
                    key={merchantConnection.merchant_id}
                    value={merchantConnection.merchant_id}
                    label={lodashGet(merchantMap, [merchantConnection.merchant_id, "name"])}
                />
            );
        });
    }, [merchants, merchantConnections]);

    const handleSelectBrand = React.useCallback((brandId: string) => {
        setSelectedBrandId(brandId);
        setBrandPopoverOpen(false);
    }, []);

    const handleCloseBrandPopover = React.useCallback(() => {
        setBrandPopoverOpen(false);
    }, []);

    const updateBrands = (brandEntities) => {
        // fetch all brands only when collections only have 2 brands or less
        if (Object.keys(brandEntities).length <= 2) {
            onFetchBrands({
                assortment_brands_only: true,
            });
        }
    };

    const handleBrandPopover = React.useCallback(() => {
        setBrandPopoverOpen(true);
        updateBrands(brandEntities);
    }, [brandEntities]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChangeRetailer = React.useCallback((e, retailer) => {
        setSelectedRetailer(retailer);
        onMerchantSelect(retailer);
        setExcludedAllocationDimensionValues({});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const isThereAtLeastOneDimensionValue = React.useCallback(() => {
        return isThereAtLeastOneDimensionValueChecker(excludedAllocationDimensionValues, merchantAllocationDimensions);
    }, [excludedAllocationDimensionValues, merchantAllocationDimensions]);

    const handleStartAssortment = React.useCallback(
        (e) => {
            e.preventDefault();
            onCreateNewCollection({
                merchantId: selectedRetailer,
                brandId: selectedBrandId,
                excludedAllocationDimensionValues,
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [excludedAllocationDimensionValues, selectedBrandId, selectedRetailer]
    );

    const shouldDisableStartAssortment = React.useMemo(() => {
        let shouldBeDisable = false;

        if (assortmentType === AssortmentType.External) {
            shouldBeDisable = !selectedBrandId;
        }

        if (!shouldBeDisable && merchantAllocationDimensions.length > 0) {
            shouldBeDisable = !isThereAtLeastOneDimensionValue();
        }
        return shouldBeDisable;
    }, [assortmentType, excludedAllocationDimensionValues, merchantAllocationDimensions, selectedBrandId]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        // Clear excluded allocation dimension values on modal open
        if (isOpen) {
            setExcludedAllocationDimensionValues({});
        }
    }, [isOpen]);

    return (
        <React.Fragment>
            <Dialog
                open={isOpen}
                onRequestClose={onDialogClose}
                title={formatMessage("Start Assortment")}
                actions={[
                    <Button
                        data-test={DATATEST.ASSORTMENTS.START_ASSORTMENT_MODAL}
                        primary={true}
                        key={"start"}
                        label={formatMessage("Start Assortment")}
                        onClick={handleStartAssortment}
                        disabled={shouldDisableStartAssortment}
                    />,
                ]}
                autoScrollBodyContent={true}
            >
                <Grid container={true}>
                    {merchantConnections.length > 1 && (
                        <Grid item={true} xs={12}>
                            <Label style={muiStyles.label}>{formatMessage("Retailer")}</Label>
                            <RadioButtonGroup
                                name="restriction-selector"
                                valueSelected={selectedRetailer}
                                onChange={handleChangeRetailer}
                            >
                                {retailerOptions}
                            </RadioButtonGroup>
                        </Grid>
                    )}
                    {assortmentType === AssortmentType.External && (
                        <Grid item={true} xs={6}>
                            <SelectWrapper
                                label={formatMessage("Selected Brand")}
                                placeholder={
                                    selectedBrandId
                                        ? brandEntities[selectedBrandId].name
                                        : formatMessage("Select Brand")
                                }
                                onClick={handleBrandPopover}
                            >
                                <BrandDropdown
                                    ids={brandIds}
                                    entities={brandEntities}
                                    anchorEl={anchorElRef.current}
                                    open={brandPopoverOpen}
                                    onRequestClose={handleCloseBrandPopover}
                                    onBrandChange={handleSelectBrand}
                                    selectedBrandId={selectedBrandId}
                                    popOverStyle={muiStyles.popover}
                                    assortmentOnly={true}
                                    isFetchingBrands={isFetchingBrands}
                                />
                                <div ref={anchorElRef} />
                            </SelectWrapper>
                        </Grid>
                    )}
                    {merchantAllocationDimensions.map((allocationDimension) => (
                        <React.Fragment key={allocationDimension.id}>
                            <Grid item={true} xs={6}>
                                <ValuesSelect
                                    merchantAllocationDimension={allocationDimension}
                                    onChange={handleOnAllocationDimensionValuesChangeFactory(
                                        allocationDimension,
                                        setExcludedAllocationDimensionValues
                                    )}
                                />
                            </Grid>
                        </React.Fragment>
                    ))}
                </Grid>
            </Dialog>
        </React.Fragment>
    );
});
